import { useAtomValue } from "jotai";
import { useState } from "react";
import { PhotoEditDialog } from "@/modules/plus/photos/components/PhotoEditDialog";
import type { GalleryPhoto } from "@/modules/plus/photos/plus.photos.atoms";
import { atoms } from "@/state";

export const GalleryGrid: React.FC = () => {
  const photos = useAtomValue(atoms.photos.galleryFiltered);
  const isLoading = useAtomValue(atoms.photos.uploadedQuery).isLoading;
  // The dialog owns "open/closed"; the grid owns "which photo is being
  // edited." Keeping the selection here means the dialog can close
  // (onOpenChange -> false) without dropping the enter/exit animation.
  const [selected, setSelected] = useState<GalleryPhoto | null>(null);

  if (isLoading) {
    return <p className="text-sm text-gray-500 dark:text-gray-400">Loading photos…</p>;
  }
  if (photos.length === 0) {
    return (
      <p className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-6 text-center text-sm text-gray-600 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-400">
        No photos match the current filters.
      </p>
    );
  }

  return (
    <>
      <ul className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6">
        {photos.map((p) => (
          <li key={p.imagePath}>
            <GalleryTile photo={p} onOpen={() => setSelected(p)} />
          </li>
        ))}
      </ul>
      <PhotoEditDialog
        photo={selected}
        isOpen={selected !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelected(null);
          }
        }}
      />
    </>
  );
};

interface GalleryTileProps {
  photo: GalleryPhoto;
  onOpen: () => void;
}

// uploaded_at can be missing or malformed on legacy entries (see
// allPhotosFlat); render nothing rather than "Invalid Date".
const formatUploadedAt = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) {
    return "";
  }
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(d);
};

const GalleryTile: React.FC<GalleryTileProps> = ({ photo, onOpen }) => (
  <button
    type="button"
    onClick={onOpen}
    className="group flex w-full flex-col overflow-hidden rounded-lg border border-gray-200 bg-white text-left shadow-sm transition hover:border-blue-400 hover:shadow-md dark:border-gray-800 dark:bg-gray-900 dark:hover:border-blue-600"
    aria-label={`Edit photo ${photo.code}`}
  >
    <div className="relative aspect-square overflow-hidden bg-gray-50 dark:bg-gray-950">
      <img
        src={photo.url}
        alt={photo.caption ?? photo.code}
        className="h-full w-full object-contain transition-transform group-hover:scale-[1.02]"
        loading="lazy"
      />
      <span className="absolute bottom-1 left-1 right-1 truncate rounded bg-black/70 px-1.5 py-0.5 text-center font-mono text-[11px] text-white">
        {photo.code}
      </span>
    </div>
    <div className="flex flex-col gap-0.5 px-2 py-1.5">
      <span
        className="truncate text-xs text-gray-700 dark:text-gray-300"
        title={photo.caption ?? ""}
      >
        {photo.caption || (
          <span className="italic text-gray-400 dark:text-gray-600">no caption</span>
        )}
      </span>
      <span className="flex items-baseline justify-between gap-2 text-[11px] text-gray-500 dark:text-gray-400">
        <span className="truncate">{photo.uploadedBy}</span>
        {formatUploadedAt(photo.uploadedAt) !== "" && (
          <span className="shrink-0 text-gray-400 dark:text-gray-500" title={photo.uploadedAt}>
            {formatUploadedAt(photo.uploadedAt)}
          </span>
        )}
      </span>
    </div>
  </button>
);
